import React, { useState, useEffect } from 'react';

// Utils
import { SortDirection } from 'ka-table/enums';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import PermissionsGate, { isGranted } from '../../../../../utils/gate/permissionsGate';
import { useAppContext } from '../../../../../store/AppContext';
import SCOPE from '../../../../../utils/gate/scope';
import languages from '../../../../../constants/languages';
import useNotification from '../../../../../utils/hooks/useNotification';

// Icons
import { ReactComponent as PlusIcon } from '../../../../../assets/icons/plus.svg';

// Services
import useCampService from '../../../../../services/campService';

// Components
import SkeletonLoader from '../../../../molecules/SkeletonLoader/SkeletonLoader';
import GenericTable from '../../../../tables/GenericTable/GenericTable';
import EmptyList from '../../../../organisms/EmptyList/EmptyList';
import DropdownActions from '../../../../molecules/DropdownActions/DropdownActions';
import Button from '../../../../atoms/Button/Button';

function Camps() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const location = useLocation();
  const navigate = useNavigate();
  const [{ user }] = useAppContext();

  const { displayNotification } = useNotification();

  const { getAllCamps } = useCampService();

  const [camps, setCamps] = useState([]);

  useEffect(() => {
    if (location?.state) {
      const notif = location.state;
      displayNotification({ message: t(notif) });
      navigate(location.pathname, { replace: true });
    }
  }, []);

  const { isFetching } = useQuery(
    ['getAllCamps'],
    () => getAllCamps(),
    {
      onSuccess: (res) => setCamps(res?.data?.camps || []),
    },
  );

  const columns = {
    name: {
      key: 'name', title: t('camps.table.columns.name'), sortDirection: SortDirection.Ascend, bold: true,
    },
    title: { key: 'title', title: t('camps.table.columns.title') },
    actions: { key: 'actions', title: t('camps.table.columns.actions') },
  };

  const getCampRow = (camp, index) => ({
    id: index,
    name: camp.name,
    title: camp.translations.find((tr) => tr.language === languages.en_US)?.title,
    actions: (
      <div className="d-flex mobile-d-col mobile-a-end f-wrap">
        <DropdownActions
          id={`campsActionsEdit${camp.id}`}
          rightAlign
          label={t('camps.table.actions.edit.label')}
          options={[
            {
              label: t('camps.table.actions.edit.options.marketing'),
              href: `/products/camps/marketing?id=${camp.id}`,
            },
            {
              label: t('camps.table.actions.edit.options.logistic'),
              href: `/products/camps/logistic?id=${camp.id}`,
            },
          ]}
          className="m-r-30 mobile-m-r-0"
        />

        <DropdownActions
          id={`campsActionsTranslate${camp.id}`}
          rightAlign
          label={t('camps.table.actions.translate.label')}
          options={[
            {
              label: t('camps.table.actions.translate.options.french'),
              href: `/products/camps/translate?id=${camp.id}&lang=${languages.fr_FR}`,
            },
            {
              label: t('camps.table.actions.translate.options.english'),
              href: `/products/camps/translate?id=${camp.id}&lang=${languages.en_US}`,
            },
          ]}
        />
      </div>
    ),
  });

  return (
    (isFetching) ? (
      <>
        <SkeletonLoader height={60} className="m-b-20" />
        <SkeletonLoader height={300} />
      </>
    ) : (
      <>
        {camps?.length ? (
          <GenericTable
            columns={[columns.name, columns.title, columns.actions]}
            rows={camps.map((camp, index) => getCampRow(camp, index))}
            isSearchable
            searchPlaceholder={t('camps.table.search')}
          />
        ) : (
          <EmptyList
            imageTheme="products"
            title={t('camps.table.empty.title')}
            text={`
              ${t('camps.table.empty.text')} 
              ${isGranted(user, [SCOPE.create_ownEst_camp]) ? t('camps.table.empty.add') : ''}
            `}
          />
        )}

        {!isFetching ? (
          <PermissionsGate scopes={[SCOPE.create_ownEst_camp]}>
            <div className="d-flex a-center j-end sticky-actions mobile-a-stretch">
              <a href="/products/camps/new">
                <Button full={isMobile}>
                  <PlusIcon className="m-r-10" />
                  {t('camps.add')}
                </Button>
              </a>
            </div>
          </PermissionsGate>
        ) : null}
      </>
    )
  );
}

Camps.propTypes = {};

export default Camps;
